import { Track } from "@/src/lib/tracking";
import debounce from "lodash-es/debounce";
import { formatNumberWithZeroCount } from "utils";
import {
  DrawLinesParams,
  ICreateGlowFilterParams,
  IDrawGridLinesParams,
  IDrawLine,
  IDrawLineShadowParams,
  IDrawTickLabelParams,
  IGetScale,
  IHandleChartResizeParams,
  ScaleLinear,
  SVGEl,
  SVGGEl,
} from "./type";

/**
 * 这个文件为绘图相关的辅助函数
 */

const transitionDuration = 150; // 过度时的动画时长
const TICK_COUNT = 5; // 刻度数量
const AXIS_TEXT_SIZE = "10px"; // 坐标轴文本大小
const DEFAULT_COLOR = "#fff";
const DEFAULT_LINE_WIDTH = 2;

export const getTransition = (duration = transitionDuration) => {
  return window.d3.transition().duration(duration);
};

// 处理图表自适应尺寸的函数
export const handleChartResize = (params: IHandleChartResizeParams) => {
  // 创建防抖函数
  const debouncedDrawChart = debounce(params.drawChart, 60);

  // 监听容器大小变化
  const resizeObserver = new ResizeObserver(() => {
    debouncedDrawChart();
  });
  resizeObserver.observe(params.el);
  return {
    cleanup: () => {
      resizeObserver.disconnect();
    },
  };
};

// 获取比例尺
export const getScale: IGetScale = (yExtent, range) => {
  let max = yExtent?.[1] ?? 0;
  let min = yExtent?.[0] ?? 0;
  if (max === min) {
    max = min + 10;
    min = min - 10;
  }
  return window.d3.scaleLinear().domain([min, max]).range(range);
};

// 创建线条生成器
const createLineGenerator = (
  xScale: ScaleLinear,
  yScale: ScaleLinear,
  smooth = false,
) => {
  const lineGen = window.d3
    .line<{ x: number; y: number } | number>()
    .x((d, i) => {
      if (typeof d !== "number" && "x" in d) {
        return xScale(d.x);
      }
      return xScale(i);
    })
    .y((d) => {
      if (typeof d !== "number" && "y" in d) {
        return yScale(d.y);
      }
      return yScale(d);
    });

  // 使用曲线插值器实现平滑圆角效果
  if (smooth) {
    lineGen.curve(window.d3.curveCatmullRom.alpha(0.5));
  }

  return lineGen;
};

// 获取折线的group
export const getSeriesGroups = (
  svg: SVGGEl,
  lines: DrawLinesParams["lines"],
) => {
  // 使用数据绑定为每个数据系列创建或更新一个组
  const seriesGroups = svg
    .selectAll<SVGGElement, DrawLinesParams["lines"][number]>(".series-group")
    .data(lines, (d) => d.name);
  // Enter selection: 为新的数据系列创建组
  const enterGroups = seriesGroups
    .enter()
    .append("g")
    .attr(
      "class",
      (d: any) => `series-group line-${d.name.replace(/\s/g, "_")}`,
    );
  // Exit selection: 移除不再存在的数据系列的组
  seriesGroups.exit().remove();
  // Merge + Update: 对所有现有和新的组执行操作
  const allGroups = enterGroups.merge(seriesGroups as any);
  return allGroups;
};

// 绘制折线
export const drawLine = (params: IDrawLine) => {
  const { svg, xScale, yScale, line, color, lineWidth, filterId, smooth } =
    params;
  const t = getTransition();
  // 将数据分为需要画线和只需要画点的两组
  const lineSegments = line.filter((d) => d.data.length > 1);
  const pointSegments = line.filter((d) => d.data.length === 1);

  // --- 1. 绘制折线 (数据点 > 1) ---
  const lineGenerator = createLineGenerator(xScale, yScale, smooth);
  const paths = svg
    .selectAll<SVGPathElement, (typeof lineSegments)[0]>(".line-segment")
    .data(lineSegments, (_d, i) => i);
  paths.exit().transition(t).attr("opacity", 0).remove();
  const enterPaths = paths
    .enter()
    .append("path")
    .attr("class", "line-segment")
    .attr("fill", "none")
    .attr("opacity", 0);

  const mergedPaths = paths
    .merge(enterPaths)
    .transition(t)
    .attr("opacity", 1)
    .attr("stroke", (d) => d.color || color || DEFAULT_COLOR)
    .attr("stroke-width", (d) => d.lineWidth || lineWidth || DEFAULT_LINE_WIDTH)
    .attr("stroke-linejoin", smooth ? "round" : "miter") // 平滑模式使用圆角拐角
    .attr("stroke-linecap", smooth ? "round" : "butt") // 平滑模式使用圆角端点
    .attr("d", (d) => lineGenerator(d.data));

  // 应用发光滤镜（如果提供了 filterId）
  if (filterId) {
    mergedPaths.attr("filter", `url(#${filterId})`);
  } else {
    mergedPaths.attr("filter", null);
  }

  // --- 2. 绘制孤立的点 (数据点 === 1) ---
  const circles = svg
    .selectAll<SVGCircleElement, (typeof pointSegments)[0]>(".point-segment")
    .data(pointSegments, (_d, i) => i);

  circles.exit().transition(t).attr("opacity", 0).remove();

  const enterCircles = circles
    .enter()
    .append("circle")
    .attr("class", "point-segment")
    .attr("r", 1.5) // 半径为1
    .attr("opacity", 0);

  const mergedCircles = circles
    .merge(enterCircles)
    .transition(t)
    .attr("opacity", 1)
    .attr("fill", (d) => d.color || color || DEFAULT_COLOR)
    .attr("cx", (d) => xScale(d.data[0].x))
    .attr("cy", (d) => yScale(d.data[0].y));

  // 应用发光滤镜到孤立点（如果提供了 filterId）
  if (filterId) {
    mergedCircles.attr("filter", `url(#${filterId})`);
  } else {
    mergedCircles.attr("filter", null);
  }
};

// 绘制折线的阴影
export const drawLineShadow = (params: IDrawLineShadowParams) => {
  const { svg, xScale, yScale, line, color, name: sourceName, smooth } = params;
  const name = sourceName.replace(/\s/g, "_");
  const t = getTransition();
  // 使用 .join() 确保 <defs> 元素存在且类型正确
  const defs = svg.selectAll("defs").data([null]).join("defs");
  const shadowSegments = line.filter((item) => item.shadow);
  // --- 管理渐变 ---
  const gradients = defs
    .selectAll<SVGLinearGradientElement, (typeof shadowSegments)[0]>(
      `.area-gradient-${window.CSS.escape(name)}`,
    )
    .data(shadowSegments, (_d, i) => i); // 使用索引作为key
  gradients.exit().remove();
  const enterGradients = gradients
    .enter()
    .append("linearGradient")
    .attr("class", `area-gradient-${name}`)
    .attr("id", (_d, i) => `area-gradient-${name}-${i}`)
    .attr("gradientUnits", "userSpaceOnUse")
    .attr("x1", "0%")
    .attr("y1", "0%")
    .attr("x2", "0%")
    .attr("y2", "100%");

  enterGradients
    .append("stop")
    .attr("class", "stop1")
    .attr("offset", "0%")
    .attr("stop-color", (d) => d.color || color || DEFAULT_COLOR)
    .attr("stop-opacity", (d) => d.shadowOpacity ?? 0.2);

  enterGradients
    .append("stop")
    .attr("class", "stop2")
    .attr("offset", "80%")
    .attr("stop-color", (d) => d.color || color || DEFAULT_COLOR)
    .attr("stop-opacity", 0);

  const updateGradients = gradients.merge(enterGradients);
  updateGradients
    .select(".stop1")
    .transition(t)
    .attr("stop-color", (d) => d.color || color || DEFAULT_COLOR)
    .attr("stop-opacity", (d) => d.shadowOpacity ?? 0.2);
  updateGradients
    .select(".stop2")
    .transition(t)
    .attr("stop-color", (d) => d.color || color || DEFAULT_COLOR);
  // --- 管理阴影路径 ---
  const areaGenerator = window.d3
    .area<{ x: number; y: number }>()
    .x((d) => xScale(d.x))
    .y0(yScale.range()[0]) // 总是从图表底部开始
    .y1((d) => yScale(d.y));

  if (smooth) {
    areaGenerator.curve(window.d3.curveCatmullRom.alpha(0.5));
  }

  const group = createOrSelectGroup(svg, `shadow-${name}`);

  const paths = group
    .selectAll<SVGPathElement, (typeof shadowSegments)[0]>("path")
    .data(shadowSegments, (_d, i) => i);

  paths.exit().transition(t).attr("opacity", 0).remove();

  const enterPaths = paths
    .enter()
    .append("path")
    .attr("fill", (_d, i) => `url(#area-gradient-${name}-${i})`)
    .attr("stroke", "none")
    .attr("opacity", 0)
    .attr("d", (d) => areaGenerator(d.data));

  paths
    .merge(enterPaths)
    .transition(t)
    .attr("opacity", 1)
    .attr("fill", (_d, i) => `url(#area-gradient-${name}-${i})`)
    .attr("d", (d) => areaGenerator(d.data));
};

// 获取容器的大小
export const getSizes = (el: HTMLElement) => {
  try {
    const containerWidth = el.clientWidth;
    const containerHeight = el.clientHeight;
    return {
      containerWidth,
      containerHeight,
    };
  } catch (error) {
    Track.log("error", "utils", "Error getting sizes:", {
      cause: String(error),
    });
    return {
      containerWidth: 0,
      containerHeight: 0,
    };
  }
};

// 获取SVG 即绘图的承载容器
export const getSVG = (el: HTMLElement, name = "chart"): SVGEl | null => {
  if (!window.d3?.select) {
    Track.log("warn", "getSVG", "d3 is unavailable, skip drawing");
    return null;
  }
  try {
    const selection = window.d3.select(el);
    const { containerHeight } = getSizes(el);
    let svg = selection.select<SVGSVGElement>(`svg.${name}`);
    if (svg.empty()) {
      svg = selection
        .append("svg")
        .attr("class", name)
        .attr("width", "100%")
        .attr("height", containerHeight);
    } else {
      svg.attr("height", containerHeight);
    }
    return svg;
  } catch (error) {
    Track.log("error", "utils", "Error getting SVG:", {
      arg1: String(window.d3),
      arg2: String(error),
    });
    return null;
  }
};

// 创建或选择一个g元素
export const createOrSelectGroup = (
  selection: SVGEl | SVGGEl,
  className: string,
): SVGGEl => {
  let group = selection.select<SVGGElement>(
    `g.${window.CSS.escape(className)}`,
  );
  if (group.empty()) {
    group = selection.append("g").attr("class", className);
  }
  return group;
};

/**
 * 将颜色字符串转换为 RGB 归一化值（0-1 范围）
 * 支持 hex (#RGB, #RRGGBB) 和 rgb/rgba 格式
 */
const parseColorToNormalizedRGB = (
  color: string,
): { r: number; g: number; b: number } => {
  // 默认颜色（青色）
  const defaultColor = { r: 0.537, g: 0.917, b: 0.969 };

  if (!color) {
    return defaultColor;
  }

  // 处理 hex 格式
  if (color.startsWith("#")) {
    let hex = color.slice(1);
    // 处理简写形式 #RGB -> #RRGGBB
    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((c) => c + c)
        .join("");
    }
    if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16) / 255;
      const g = parseInt(hex.slice(2, 4), 16) / 255;
      const b = parseInt(hex.slice(4, 6), 16) / 255;
      return { r, g, b };
    }
  }

  // 处理 rgb/rgba 格式
  const rgbMatch = color.match(
    /rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*[\d.]+)?\s*\)/,
  );
  if (rgbMatch) {
    return {
      r: parseInt(rgbMatch[1], 10) / 255,
      g: parseInt(rgbMatch[2], 10) / 255,
      b: parseInt(rgbMatch[3], 10) / 255,
    };
  }

  return defaultColor;
};

/**
 * 创建发光滤镜
 * 基于 SVG Filter 实现，通过高斯模糊和颜色矩阵创建发光效果
 */
export const createGlowFilter = (params: ICreateGlowFilterParams): string => {
  const { svg, id, color, blur = 3, opacity = 1 } = params;
  const filterId = `glow-filter-${id}`;

  // 确保 <defs> 元素存在
  let defs = svg.select<SVGDefsElement>("defs");
  if (defs.empty()) {
    defs = svg.append("defs");
  }

  // 检查滤镜是否已存在，如果存在则移除后重新创建
  defs.select(`#${window.CSS.escape(filterId)}`).remove();

  // 解析颜色为 RGB 归一化值
  const { r, g, b } = parseColorToNormalizedRGB(color);

  // 创建滤镜
  const filter = defs
    .append("filter")
    .attr("id", filterId)
    .attr("filterUnits", "userSpaceOnUse")
    .attr("color-interpolation-filters", "sRGB")
    // 扩展滤镜区域以容纳模糊效果
    .attr("x", "-50%")
    .attr("y", "-50%")
    .attr("width", "200%")
    .attr("height", "200%");

  // 步骤1: 创建透明背景
  filter
    .append("feFlood")
    .attr("flood-opacity", "0")
    .attr("result", "BackgroundImageFix");

  // 步骤2: 高斯模糊 (直接使用 SourceAlpha 以保留抗锯齿)
  filter
    .append("feGaussianBlur")
    .attr("in", "SourceAlpha")
    .attr("stdDeviation", blur)
    .attr("result", "blurredAlpha");

  // 步骤3: 复合操作 (直接使用 blurredAlpha，不挖空，让发光层作为背景)
  // 如果需要挖空效果，可保留 out 操作，但对于描边发光，直接叠加通常效果更好且无锯齿
  // 这里我们为了避免锯齿，不再使用 operator="out" 挖空原图形，
  // 而是直接使用高斯模糊后的结果作为发光层，原图形在步骤 6 中叠加在发光层之上

  // 步骤4: 设置发光颜色
  // feColorMatrix values 格式: R通道 G通道 B通道 A通道 偏移
  // 每行5个值，共4行（RGBA）
  const colorMatrix = `0 0 0 0 ${r} 0 0 0 0 ${g} 0 0 0 0 ${b} 0 0 0 ${opacity} 0`;
  filter
    .append("feColorMatrix")
    .attr("in", "blurredAlpha") // 确保输入是模糊后的 Alpha
    .attr("type", "matrix")
    .attr("values", colorMatrix)
    .attr("result", "coloredGlow"); // 命名结果

  // 步骤5: 混合发光效果和背景
  filter
    .append("feBlend")
    .attr("mode", "normal")
    .attr("in", "coloredGlow") // 使用上色后的发光层
    .attr("in2", "BackgroundImageFix")
    .attr("result", "effect1_dropShadow");

  // 步骤6: 将原图形叠加在发光效果上方
  filter
    .append("feBlend")
    .attr("mode", "normal")
    .attr("in", "SourceGraphic")
    .attr("in2", "effect1_dropShadow")
    .attr("result", "shape");

  return filterId;
};

const formatLargeOrSmallNumber = (
  ticks: number[],
): Record<
  number,
  string | { integerPart: string; zeroCount: number; leftDigits: string }
> => {
  if (ticks.every((tick) => tick < 1 && tick > 0)) {
    return ticks.reduce<
      Record<
        number,
        { integerPart: string; zeroCount: number; leftDigits: string }
      >
    >((acc, curr) => {
      acc[curr] = formatNumberWithZeroCount({
        numStr: curr.toFixed(20),
        decimals: 2,
        minZeroCount: 2,
      });
      return acc;
    }, {});
  }
  return window.D3LineChart.D3LineChart.formatLargeNumber(ticks);
};
// 生成刻度
export const generateTicks = (params: {
  scale: ScaleLinear;
  tickCount?: number;
  formatter?: (ticks: number[]) => Record<number, string>;
}) => {
  const { scale, tickCount = TICK_COUNT, formatter } = params;
  const ticks = scale.nice(tickCount).ticks(tickCount); // [102000, 104000] [0.05, 0.01]
  // 使用自定义格式化函数或默认的 formatLargeNumber
  const tickLabelMap = formatter
    ? formatter(ticks)
    : formatLargeOrSmallNumber(ticks); // {102000: "102K", 104000: "104K"} {}
  return {
    ticks,
    tickLabelMap,
  };
};

// 测算一组文本中，最长的文本的宽度
export const getMaxTextWidth = (
  texts: (
    string | { integerPart: string; zeroCount: number; leftDigits: string }
  )[],
  svg: SVGEl,
  fontSize = AXIS_TEXT_SIZE,
): number => {
  const tempText = svg
    .append("text")
    .attr("class", "temp-text")
    .attr("font-size", fontSize)
    .style("visibility", "hidden");
  const getTextSize = (text: string): { width: number; height: number } => {
    tempText.text(text);
    const { width, height } = (
      tempText.node() as SVGTextElement
    )?.getBBox() || { width: 0, height: 0 };
    return { width, height };
  };
  const textSizes = texts.map((t) => {
    if (typeof t === "string") {
      return getTextSize(t);
    }
    let text = t.integerPart;
    if (t.zeroCount > 0) {
      text += `.0${t.zeroCount}`;
    }
    if (t.leftDigits?.length) {
      text += `.${t.leftDigits}`;
    }
    return getTextSize(text);
  });
  const maxWidth = Math.max(...textSizes.map((v) => v.width));
  tempText.remove();
  return maxWidth;
};

// 绘制网格线
export const drawGridLines = (params: IDrawGridLinesParams) => {
  const {
    direction = "horizontal",
    start,
    end,
    ticks,
    scale,
    svg,
    color,
    lineWidth = 1,
    lineType = "dashed",
  } = params;
  const t = getTransition();
  // 1. Data-join
  const gridLines = svg
    .selectAll<SVGLineElement, number>(`.grid-line-${direction}`)
    .data(ticks, (d) => d);

  // 定义一个函数来配置所有属性，以便复用
  const configureAttributes = (selection: any) => {
    selection
      .attr("x1", (d: number) =>
        direction === "horizontal" ? start : scale(d),
      )
      .attr("x2", (d: number) => (direction === "horizontal" ? end : scale(d)))
      .attr("y1", (d: number) =>
        direction === "horizontal" ? scale(d) : start,
      )
      .attr("y2", (d: number) => (direction === "horizontal" ? scale(d) : end))
      .attr("stroke", (_: any, i: number) => {
        if (typeof color === "string") {
          return color;
        }
        return color[i] || color.at(-1)!;
      })
      .attr("stroke-width", (_: any, i: number) => {
        if (typeof lineWidth === "number") {
          return lineWidth;
        }
        return lineWidth[i] || lineWidth.at(-1)!;
      })
      .attr("stroke-dasharray", (_: any, i: number) => {
        let currentLineType: "solid" | "dashed" = "dashed";
        if (typeof lineType === "string") {
          currentLineType = lineType;
        } else if (Array.isArray(lineType)) {
          currentLineType = lineType[i] || lineType.at(-1) || "dashed";
        }
        return currentLineType === "dashed" ? "3,3" : "none";
      });
  };

  // Exit selection: 移除旧的网格线
  gridLines.exit().transition(t).attr("opacity", 0).remove();

  // Enter selection: 添加新的网格线并设置初始状态
  const enterGridLines = gridLines
    .enter()
    .append("line")
    .attr("class", `grid-line-${direction}`)
    .call(configureAttributes)
    .attr("opacity", 0);

  // Update + Enter selection: 更新所有网格线到最终状态
  gridLines
    .merge(enterGridLines)
    .transition(t)
    .call(configureAttributes)
    .attr("opacity", 1);
};

// 绘制刻度文本
export const drawTickLabel = (params: IDrawTickLabelParams) => {
  const {
    svg,
    scale,
    direction,
    fixedPosition,
    color,
    fontSize,
    ticks,
    tickLabelMap,
  } = params;
  const t = getTransition();
  // 当刻度列表为空时，清除所有现有标签
  if (!ticks?.length) {
    svg
      .selectAll(`.axis-label-${direction}`)
      .transition(t)
      .attr("opacity", 0)
      .remove();
    return;
  }
  // 1. 数据绑定，使用 index 作为 key
  const labels = svg
    .selectAll<SVGTextElement, number>(`.axis-label-${direction}`)
    .data(ticks, (d) => d);
  // 2. Exit selection: 移除旧标签
  labels.exit().transition(t).attr("opacity", 0).remove();
  // 定义一个函数来配置所有属性，以便复用
  const configureAttributes = (selection: any) => {
    selection
      .attr("y", (d: number) =>
        direction === "horizontal" ? fixedPosition : scale(d),
      )
      .attr("x", (d: number) =>
        direction === "horizontal" ? scale(d) : fixedPosition,
      )
      .attr("text-anchor", (d: number) => {
        if (direction !== "horizontal") {
          return "end";
        }
        if (d === scale.domain()[0]) {
          return "start";
        }
        if (d === scale.domain().at(-1)) {
          return "end";
        }
        return "middle";
      })
      .attr("fill", (_: any, i: number) => {
        if (typeof color === "string") {
          return color;
        }
        return color[i] || color.at(-1)!;
      })
      .attr("font-size", fontSize);
  };

  const renderTickLabel = (selection: any, d: number) => {
    const v = tickLabelMap?.[d];
    if (!v) {
      selection.selectAll("tspan").remove();
      selection.text("");
      return;
    }

    if (typeof v === "string") {
      selection.selectAll("tspan").remove();
      selection.text(v);
      return;
    }

    const rawIntegerPart = (v as any)?.integerPart;
    const rawZeroCount = (v as any)?.zeroCount;
    const rawLeftDigits = (v as any)?.leftDigits;
    const rawIsSuccess = (v as any)?.isSuccess;

    const isSuccess =
      typeof rawIsSuccess === "boolean" ? rawIsSuccess : rawIsSuccess == null;
    if (!isSuccess) {
      selection.selectAll("tspan").remove();
      selection.text("");
      return;
    }

    const integerPart =
      rawIntegerPart == null ? "" : String(rawIntegerPart).trim();
    const leftDigits =
      rawLeftDigits == null ? "" : String(rawLeftDigits).trim();
    const zeroCountNum = Number(rawZeroCount);
    const zeroCount = Number.isFinite(zeroCountNum) ? zeroCountNum : 0;

    // 兼容：zeroCount=0 时按普通小数展示
    if (!zeroCount) {
      selection.selectAll("tspan").remove();
      const dot = leftDigits ? "." : "";
      selection.text(`${integerPart}${dot}${leftDigits}`);
      return;
    }

    const smallFontSize = Math.max(0, fontSize - 2);
    // 展示规则：0.0 + (zeroCount - 1) + leftDigits
    const shownZeroCount = Math.max(0, Math.floor(zeroCount));

    // rich label：0.0 + (small zeroCount) + leftDigits
    selection.text("");
    selection.selectAll("tspan").remove();

    selection
      .append("tspan")
      .attr("font-size", fontSize)
      .text(`${integerPart || "0"}.0`);

    if (shownZeroCount > 0) {
      selection
        .append("tspan")
        .attr("font-size", smallFontSize)
        .text(String(shownZeroCount));
    }

    selection
      .append("tspan")
      .attr("font-size", fontSize)
      .text(leftDigits || "");
  };
  // 3. Enter selection: 添加新标签
  const enterLabels = labels
    .enter()
    .append("text")
    .attr("class", `axis-label-${direction} body_xs`)
    .attr("dy", "0.32em")
    .call(configureAttributes)
    .attr("opacity", 0) // 初始透明，为淡入做准备
    .each(function (d) {
      renderTickLabel(window.d3.select(this), d);
    });
  // 4. Update + Enter selection: 更新所有标签
  labels
    .merge(enterLabels)
    .each(function (d) {
      renderTickLabel(window.d3.select(this), d);
    })
    .transition(t)
    .call(configureAttributes)
    .attr("opacity", 1);
};
